import { X } from "lucide-react";
import type { DoctorListItem, LabTestOption, MedicineEntry } from "../../services/api/admin-order.types";

interface PrescriptionPreviewProps {
  onClose: () => void;
  doctor: DoctorListItem | null;
  chiefComplaints: string;
  diagnosis: string;
  labTests: LabTestOption[];
  medicines: MedicineEntry[];
  specialInstructions: string;
}

const PrescriptionPreview: React.FC<PrescriptionPreviewProps> = ({
  onClose, doctor, chiefComplaints, diagnosis, labTests, medicines, specialInstructions,
}) => {
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center animate-fadeIn" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-[480px] w-[95%] max-h-[90vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-800">Preview</h3>
          <button className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Doctor Details */}
          {doctor && (
            <div>
              <h4 className="text-xs font-bold text-slate-800 mb-2">Doctor Details</h4>
              <div className="text-xs text-slate-700 font-semibold">{doctor.name}{doctor.doctorSpecialization ? `, ${doctor.doctorSpecialization}` : ""}</div>
              {doctor.doctorEducation && <div className="text-[0.65rem] text-slate-500">{doctor.doctorEducation}</div>}
              {doctor.registrationNumber && <div className="text-[0.65rem] text-slate-500">Reg No: {doctor.registrationNumber}</div>}
            </div>
          )}

          {/* Chief Complaints */}
          {chiefComplaints && (
            <div>
              <h4 className="text-xs font-bold text-slate-800 mb-1">Chief Complaints</h4>
              <p className="text-xs text-slate-600 leading-relaxed">{chiefComplaints}</p>
            </div>
          )}

          {/* Diagnosis */}
          {diagnosis && (
            <div>
              <h4 className="text-xs font-bold text-slate-800 mb-1">Diagnosis</h4>
              <p className="text-xs text-slate-600 leading-relaxed">{diagnosis}</p>
            </div>
          )}

          {/* Lab Tests */}
          {labTests.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-slate-800 mb-2">Lab Tests</h4>
              <div className="space-y-1">
                {labTests.map((test) => (
                  <div key={test.id} className="text-xs text-slate-600 flex items-start gap-2">
                    <span className="text-slate-400">•</span>
                    {test.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Medicines */}
          {medicines.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-slate-800 mb-2">Medicines</h4>
              <div className="space-y-3">
                {medicines.map((med, i) => (
                  <div key={`${med.medicineId}-${i}`} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-1.5">
                      <div>
                        <span className="text-xs font-semibold text-slate-800">• {med.medicineName}</span>
                        {med.medicineSalt && <span className="text-[0.6rem] text-slate-400 ml-1">({med.medicineSalt})</span>}
                      </div>
                    </div>
                    {med.dosageType && (
                      <div className="mb-1">
                        <span className="text-[0.6rem] font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">{med.dosageType}</span>
                      </div>
                    )}
                    {med.generatedText && (
                      <div className="text-[0.65rem] text-slate-600 leading-relaxed">
                        {med.generatedText}
                      </div>
                    )}
                    {med.note && (
                      <div className="mt-1.5 pt-1.5 border-t border-slate-100">
                        <span className="text-[0.6rem] font-semibold text-amber-600">Note: </span>
                        <span className="text-[0.6rem] text-slate-500">{med.note}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Special Instructions */}
          {specialInstructions && (
            <div>
              <h4 className="text-xs font-bold text-slate-800 mb-1">Special Instructions</h4>
              <p className="text-xs text-slate-600 leading-relaxed">{specialInstructions}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionPreview;
